import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { isLocalhost, isNonProduction } from "./utils";
import { StoreEnhancer } from "@reduxjs/toolkit";

/**
 * Sentry is ON for production checkout hosts by default.
 * On staging/localhost it is OFF (no noise, no PII in dev) unless you set:
 *   VITE_SENTRY_ENABLE=true
 * Optional override DSN:
 *   VITE_SENTRY_DSN=https://...@....ingest....
 * In Sentry UI filter by `environment`: production | staging | development
 */
const FALLBACK_SENTRY_DSN =
  "https://0705844aee0db743fc547e51a5c70895@o4511026044534784.ingest.de.sentry.io/4511026102272080";

function shouldRunSentry(): boolean {
  if (!isNonProduction()) return true;
  return import.meta.env.VITE_SENTRY_ENABLE === "true";
}

function resolveSentryDsn(): string | undefined {
  const fromEnv = import.meta.env.VITE_SENTRY_DSN?.trim();
  if (fromEnv) return fromEnv;
  if (!isNonProduction()) return FALLBACK_SENTRY_DSN;
  // Staging/local only when explicitly enabled — use same DSN unless VITE_SENTRY_DSN set in deploy env
  if (import.meta.env.VITE_SENTRY_ENABLE === "true") return FALLBACK_SENTRY_DSN;
  return undefined;
}

function resolveSentryEnvironment(): string {
  if (!isNonProduction()) return "production";
  if (isLocalhost()) return "development";
  return "staging";
}

// Type-safe empty enhancer
const emptyEnhancer: StoreEnhancer =
  (createStore) => (reducer, initialState) => {
    return createStore(reducer, initialState);
  };

/**
 * Creates a safe, flattened state snapshot for Sentry.
 * Avoids deep recursion that causes stack overflow on iOS WebKit.
 * iOS WebKit has ~50% smaller call stack limit than V8.
 */
function createSafeStateSnapshot(state: unknown): Record<string, unknown> | null {
  try {
    if (!state || typeof state !== "object") {
      return null;
    }

    const s = state as Record<string, unknown>;
    const order = s.order as Record<string, unknown> | undefined;
    const bankId = s.bankId as Record<string, unknown> | undefined;
    const delivery = s.delivery as Record<string, unknown> | undefined;
    const operator = s.operator as Record<string, unknown> | undefined;
    const payment = s.payment as Record<string, unknown> | undefined;
    const fetchedOrder = order?.fetchedOrder as Record<string, unknown> | undefined;
    const steps = order?.steps as Record<string, unknown> | undefined;

    return {
      order: {
        id: (fetchedOrder?.data as Record<string, unknown>)?.id ?? null,
        currentStep: steps?.currentStep ?? null,
        initializeComplete: order?.initializeComplete ?? false,
        submitLoading: order?.submitLoading ?? false,
      },
      bankId: {
        initiateBankIdLoading: bankId?.initiateBankIdLoading ?? "unknown",
        verifyBankIdSessionLoading: bankId?.verifyBankIdSessionLoading ?? "unknown",
        hasVerificationSuccess: !!bankId?.verificationSuccess,
        hasEmail: !!(bankId?.email as string),
        hasPhone: !!(bankId?.phone as string),
        error: bankId?.error ?? null,
      },
      delivery: {
        selectedMethod: delivery?.selectedMethod ?? null,
        validateAddressLoading: delivery?.validateAddressLoading ?? "unknown",
        servicePointsCount: Array.isArray(delivery?.servicePoints) ? delivery.servicePoints.length : 0,
        parcelBoxesCount: Array.isArray(delivery?.parcelBoxes) ? delivery.parcelBoxes.length : 0,
        hasSelectedServicePoint: !!delivery?.selectedServicePoint,
        hasSelectedParcelBox: !!delivery?.selectedParcelBox,
        error: delivery?.error ?? null,
      },
      operator: {
        selectedOperator: operator?.selectedOperator ?? null,
        selectedPlan: operator?.selectedPlan ?? null,
        operatorsCount: Array.isArray(operator?.operators) ? operator.operators.length : 0,
        operatorsLoading: operator?.operatorsLoading ?? false,
        error: operator?.operatorsError ?? null,
      },
      payment: {
        hasSetupIntent: !!(payment?.setupIntent as Record<string, unknown>)?.clientSecret,
        error: (payment?.setupIntent as Record<string, unknown>)?.error ?? null,
      },
    };
  } catch {
    return { error: "state_transform_failed" };
  }
}

// Create Redux enhancer with safe state transformer
const actualSentryEnhancer = Sentry.createReduxEnhancer({
  stateTransformer: (state) => {
    return createSafeStateSnapshot(state);
  },
  actionTransformer: (action) => {
    // Return only action type to avoid serializing large payloads
    return {
      type: action.type,
    };
  },
});

const sentryDsn = resolveSentryDsn();
const sentryActive = shouldRunSentry() && !!sentryDsn;

if (sentryActive && sentryDsn) {
  const isProdHost = !isNonProduction();
  Sentry.init({
    dsn: sentryDsn,
    environment: resolveSentryEnvironment(),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      /^https?:\/\/.*refurbly\..*/,
      /^https?:\/\/.*fly\.dev/,
      ...(isLocalhost() ? [/^https?:\/\/localhost(:\d+)?/] : []),
    ],
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: isProdHost ? 1.0 : 0.5,
  });
}

export const sentryReduxEnhancer = sentryActive ? actualSentryEnhancer : emptyEnhancer;
